/**
 * One repair order on the FOH Workboard. It answers, and only answers:
 * what is it, who owns it, where is it in the process, who/what are we
 * waiting on, what is happening in the shop, when is it due, where is the car.
 * Everything else is one tap away in the Command Center.
 *
 * Touch-first: every action is a real button (no hover-only controls), at
 * least 44px tall on phones.
 */
import { AdvisorInitialsBadge } from '@/components/foh/AdvisorInitialsBadge';
import { UNKNOWN_MEMBER_LABEL } from '@/lib/people/personDisplayName';
import { formatDistanceToNowStrict } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { ExternalLink, UserPlus, UserCog, Undo2, MoveRight, CarFront, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TechnicianHandoffCard } from '@/components/lifecycle/TechnicianHandoffCard';
import { TicketCustodyBadge } from '@/components/tickets/TicketCustodyBadge';
import { TicketBusinessStateBadge } from '@/components/tickets/TicketBusinessStateBadge';
import { custodyActorDisplay, type CustodyHistoryEntry } from '@/lib/tickets/lifecycle/custodyHistory';
import { describeLocation, type LocationNames } from '@/lib/location/labels';
import type { CurrentVehicleLocation } from '@/lib/location/model';
import type { TicketCustodyRow } from '@/hooks/tickets/useTicketCustody';
import { formatRoNumber, vehicleLine, type FohLane, type WorkboardCard } from '@/lib/foh/workboard/workboardModel';
import type { WorkboardVehicleActions } from '@/lib/foh/workboard/workboardActions';

const LANE_TONE: Record<FohLane, string> = {
  needs_attention: 'bg-red-500/15 text-red-700 dark:text-red-300',
  waiting: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  with_shop: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  closeout: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  upcoming: 'bg-muted text-muted-foreground',
  done_today: 'bg-muted text-muted-foreground',
};

const STAGE_LABEL: Record<string, string> = {
  Estimates: 'Estimate', Scheduled: 'Scheduled', WIP: 'WIP', Completed: 'Completed', PickedUp: 'Picked Up', Deleted: 'Deleted',
};

export interface FohWorkboardCardProps {
  card: WorkboardCard;
  handoff: CustodyHistoryEntry | null;
  location: CurrentVehicleLocation | null;
  locationNames: LocationNames;
  /** Names by user id (advisors + handoff senders). */
  names: Readonly<Record<string, string>>;
  shopTimezone: string;
  today: string;
  nowIso: string;
  canAssign: boolean;
  busy?: boolean;
  onOpen: (ticketId: string) => void;
  onAccept: (custodyTicketId: string) => void;
  onKickBack: (custodyTicketId: string) => void;
  onClaim: (ticketId: string) => void;
  onReassign: (card: WorkboardCard) => void;
  /** Oct 20 (03A / 03B): the vehicle actions this card offers (workboardVehicleActions). Absent = none. */
  vehicleActions?: WorkboardVehicleActions;
  onMoveVehicle?: (ticketId: string, card: WorkboardCard) => void;
  onPickedUp?: (ticketId: string, card: WorkboardCard) => void;
  onPickedUpIncomplete?: (ticketId: string, card: WorkboardCard) => void;
}

function when(iso: string | null | undefined, tz: string, today: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return formatInTimeZone(d, tz, 'yyyy-MM-dd') === today ? formatInTimeZone(d, tz, 'h:mm a') : formatInTimeZone(d, tz, 'EEE MMM d, h:mm a');
}

export function FohWorkboardCard({
  card, handoff, location, locationNames, names, shopTimezone, today, nowIso, canAssign, busy = false,
  onOpen, onAccept, onKickBack, onClaim, onReassign,
  vehicleActions, onMoveVehicle, onPickedUp, onPickedUpIncomplete,
}: FohWorkboardCardProps) {
  const { ro, placement, aging } = card;
  const lane = placement.lane;
  const o = ro.owner;
  const custodyStatus = ro.custody?.custody_status ?? null;
  // A person with no name anywhere reads as they do in the advisor tabs —
  // "Unknown member" — never as a role word that looks like a name.
  const advisorName = ro.advisorUserId ? (names[ro.advisorUserId] ?? UNKNOWN_MEMBER_LABEL) : null;
  // Closeout / Done open the owner (the commercial record); live work opens
  // the execution day the Dayboard is showing.
  const openId = lane === 'closeout' || lane === 'done_today' ? ro.id : ro.live.id;
  const due = when(o.due_out, shopTimezone, today);
  const finish = !due ? when(ro.live.opti_finish, shopTimezone, today) : null;
  const where = location ? describeLocation(location, locationNames).label : null;
  const tech = ro.live.technician?.name ?? null;
  const showHandoff = !!handoff && (custodyStatus === 'pending_advisor_accept' || custodyStatus === 'with_advisor');
  const who = handoff ? custodyActorDisplay(handoff.actorId, handoff.actorRole, names) : null;
  const dwell = ro.custody && (lane === 'needs_attention' || lane === 'waiting')
    ? formatDistanceToNowStrict(new Date(ro.custody.entered_at), { addSuffix: false })
    : null;
  void nowIso;

  return (
    <article
      data-testid={`foh-workboard-card-${ro.id}`}
      data-lane={lane}
      className={cn(
        'rounded-lg border bg-card p-3 space-y-2 shadow-sm',
        aging === 'urgent' && lane === 'needs_attention' && 'border-destructive/70',
        aging === 'warning' && lane === 'needs_attention' && 'border-amber-500/70',
      )}
    >
      <header className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen(openId)}
          data-testid={`foh-workboard-open-${ro.id}`}
          className="min-w-0 text-left group"
        >
          <div className="flex items-center gap-1.5 font-semibold">
            {formatRoNumber(o)}
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
          </div>
          {vehicleLine(o) && <div className="text-sm truncate">{vehicleLine(o)}</div>}
          {o.customer_name && <div className="text-sm text-muted-foreground truncate">{o.customer_name}</div>}
        </button>
        <span
          data-testid={`foh-workboard-detail-${ro.id}`}
          className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', LANE_TONE[lane])}
        >
          {placement.detail}
        </span>
      </header>

      <div className="flex items-center justify-between gap-2 text-sm">
        <div className="min-w-0 truncate" data-testid={`foh-workboard-advisor-${ro.id}`}>
          <span className="text-muted-foreground">Advisor: </span>
          {/* OCT28 #6: colour-coded initials of the primary advisor (persistent responsibility, not custody). */}
          {ro.advisorUserId && advisorName && advisorName !== UNKNOWN_MEMBER_LABEL && <AdvisorInitialsBadge userId={ro.advisorUserId} name={advisorName} className="mr-1 align-middle" />}
          {advisorName ? <span className="font-medium">{advisorName}</span> : <span className="italic text-muted-foreground">Unassigned</span>}
        </div>
        {canAssign && (
          ro.advisorUserId == null ? (
            <Button size="sm" variant="outline" className="h-9 gap-1 shrink-0" disabled={busy}
              data-testid={`foh-workboard-claim-${ro.id}`} onClick={() => onClaim(ro.id)}>
              <UserPlus className="h-3.5 w-3.5" /> Claim
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="h-9 gap-1 shrink-0" disabled={busy}
              data-testid={`foh-workboard-reassign-${ro.id}`} onClick={() => onReassign(card)} aria-label="Reassign advisor">
              <UserCog className="h-3.5 w-3.5" /> Reassign
            </Button>
          )
        )}
      </div>

      {showHandoff && who ? (
        <TechnicianHandoffCard
          handoff={{ actor: who.primary, actorRole: who.role, at: handoff!.createdAt, reason: handoff!.reason, notes: handoff!.notes }}
          custodyStatus={custodyStatus as never}
          onAccept={canAssign && ro.custody ? () => onAccept(ro.custody!.ticket_id) : undefined}
          accepting={busy}
        />
      ) : custodyStatus === 'pending_advisor_accept' && canAssign && ro.custody ? (
        <TechnicianHandoffCard handoff={null} custodyStatus="pending_advisor_accept" onAccept={() => onAccept(ro.custody!.ticket_id)} accepting={busy} />
      ) : null}

      <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 text-xs">
        <dt className="text-muted-foreground">Production</dt>
        <dd data-testid={`foh-workboard-stage-${ro.id}`}>{ro.lifecycleStage ? STAGE_LABEL[ro.lifecycleStage] ?? ro.lifecycleStage : '—'}</dd>
        {(tech || lane === 'with_shop') && (<>
          <dt className="text-muted-foreground">Current</dt>
          <dd>{[tech, placement.detail].filter(Boolean).join(' · ')}</dd>
        </>)}
        {where && (<>
          <dt className="text-muted-foreground">Location</dt>
          <dd data-testid={`foh-workboard-location-${ro.id}`}>{where}</dd>
        </>)}
        {(due || finish) && (<>
          <dt className="text-muted-foreground">{due ? 'Due' : 'OptiFinish'}</dt>
          <dd>{due ?? finish}</dd>
        </>)}
        {lane === 'upcoming' && ro.live.scheduled_date && (<>
          <dt className="text-muted-foreground">Scheduled</dt>
          <dd>{String(ro.live.scheduled_date).slice(0, 10)}</dd>
        </>)}
      </dl>

      <footer className="flex flex-wrap items-center gap-1.5">
        {ro.custody && <TicketCustodyBadge custody={ro.custody as unknown as TicketCustodyRow} />}
        {ro.businessState && ro.businessState !== 'draft' && <TicketBusinessStateBadge state={ro.businessState} />}
        {dwell && (
          <span className={cn('text-xs', aging === 'urgent' ? 'text-destructive font-semibold' : 'text-muted-foreground')}>
            {aging === 'urgent' ? `URGENT — ${dwell}` : dwell}
          </span>
        )}
        {custodyStatus === 'with_advisor' && canAssign && ro.custody && (
          <Button size="sm" variant="outline" className="ml-auto h-9 gap-1" disabled={busy}
            data-testid={`foh-workboard-kickback-${ro.id}`} onClick={() => onKickBack(ro.custody!.ticket_id)}>
            <Undo2 className="h-3.5 w-3.5" /> Kick Back to Tech
          </Button>
        )}
      </footer>

      {vehicleActions && (vehicleActions.move || vehicleActions.pickedUp || vehicleActions.pickedUpIncomplete) && (
        <div className="flex flex-wrap gap-1.5 border-t pt-2" data-testid={`foh-workboard-vehicle-actions-${ro.id}`}>
          {vehicleActions.pickedUp && onPickedUp && (
            <Button size="sm" className="h-11 gap-1 sm:h-9" disabled={busy}
              data-testid={`foh-workboard-picked-up-${ro.id}`} onClick={() => onPickedUp(vehicleActions.pickedUp!.ticketId, card)}>
              <CarFront className="h-3.5 w-3.5" /> Customer Picked Up
            </Button>
          )}
          {vehicleActions.move && onMoveVehicle && (
            <Button size="sm" variant="outline" className="h-11 gap-1 sm:h-9" disabled={busy}
              data-testid={`foh-workboard-move-${ro.id}`} onClick={() => onMoveVehicle(vehicleActions.move!.ticketId, card)}>
              <MoveRight className="h-3.5 w-3.5" /> Move Vehicle
            </Button>
          )}
          {vehicleActions.pickedUpIncomplete && onPickedUpIncomplete && (
            <Button size="sm" variant="outline" className="h-11 gap-1 sm:h-9" disabled={busy}
              data-testid={`foh-workboard-picked-up-incomplete-${ro.id}`}
              onClick={() => onPickedUpIncomplete(vehicleActions.pickedUpIncomplete!.ticketId, card)}>
              <LogOut className="h-3.5 w-3.5" /> Picked Up — Work Incomplete
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
